import { Region } from '@compumark/order-management-client';

const classifyString = (str: string): number =>
  !str
    ? 4
    : /^[a-zA-Z]/.test(str)
    ? 3
    : /^\d/.test(str)
    ? 2
    : /^[^a-zA-Z0-9]/.test(str)
    ? 1
    : 0;

const normalizeString = (str: string): string =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const normalizeLabel = (label: string): string =>
  label.replace(/\s*,\s*/g, ', ').trim();

export const compareRegions = (a: Region, b: Region): number => {
  const labelA = normalizeLabel(a.label || '');
  const labelB = normalizeLabel(b.label || '');

  const categoryA = classifyString(labelA);
  const categoryB = classifyString(labelB);

  if (categoryA !== categoryB) {
    return categoryA - categoryB;
  }

  const labelComparison = labelA.localeCompare(labelB, undefined, {
    sensitivity: 'base',
  });

  if (labelComparison === 0) {
    return a.limited === b.limited ? 0 : a.limited ? 1 : -1;
  }

  return labelComparison;
};

// Extract, filter, and sort regions
const getSortedRegions = (regions: Region[]): Region[] =>
  regions.filter((region) => region?.label).sort(compareRegions);

export function regionComparator(valueA: Region[], valueB: Region[]): number {
  const sortedA = getSortedRegions(valueA);
  const sortedB = getSortedRegions(valueB);

  const firstComparison = compareRegions(
    sortedA[0] || { label: '', limited: false },
    sortedB[0] || { label: '', limited: false },
  );

  if (firstComparison === 0) {
    for (let i = 0; i < Math.min(sortedA.length, sortedB.length); i++) {
      const labelComparison = compareRegions(sortedA[i], sortedB[i]);
      if (labelComparison !== 0) {
        return labelComparison;
      }
    }
    return sortedA.length - sortedB.length;
  }

  return firstComparison;
}

export const regionSetFilterComparator = (
  a: Region | null,
  b: Region | null,
): number =>
  compareRegions(
    a ?? { label: '', limited: false },
    b ?? { label: '', limited: false },
  );

export const alertTitleComparator = (valueA: any, valueB: any): number =>
  genericComparator(valueA?.title || '', valueB?.title || '');

export const genericComparator = (valueA: string, valueB: string): number => {
  const normalizedA = normalizeString(valueA || '');
  const normalizedB = normalizeString(valueB || '');

  const categoryA = classifyString(normalizedA);
  const categoryB = classifyString(normalizedB);

  if (categoryA !== categoryB) {
    return categoryA - categoryB;
  }

  return normalizedA.localeCompare(normalizedB, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

export function customComparator(valueA: string, valueB: string): number {
  const normalize = (value: string) =>
    value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return normalize(valueA).localeCompare(normalize(valueB), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export function assigneeFilterComparator(a: string, b: string): number {
  const valueALower = a ? a.toLowerCase().trim() : 'unassigned';
  const valueBLower = b ? b.toLowerCase().trim() : 'unassigned';

  // "Unassigned" at the top of the filter dropdown
  if (valueALower === 'unassigned') {
    return -1;
  }
  if (valueBLower === 'unassigned') {
    return 1;
  }
  // Alphabetical sorting
  return valueALower.localeCompare(valueBLower);
}

export function dateComparator(
  date1: string | null,
  date2: string | null,
): number {
  // Handle null or empty dates
  if (!date1 && !date2) {
    return 0;
  }
  if (!date1) {
    return 1;
  } // Null/empty values at the end
  if (!date2) {
    return -1;
  }

  const d1 = new Date(date1);
  const d2 = new Date(date2);

  return d1.getTime() - d2.getTime();
}

export function assigneeSortingComparator(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  // treat null/undefined as "Unassigned"
  const valueA = a ? a.toLowerCase().trim() : 'unassigned';
  const valueB = b ? b.toLowerCase().trim() : 'unassigned';
  return valueA.localeCompare(valueB);
}

export const dateFilterComparator = (
  filterLocalDateAtMidnight: Date,
  cellValue: any,
): number => {
  const cellDate = new Date(cellValue);
  if (cellDate < filterLocalDateAtMidnight) {
    return -1;
  } else if (cellDate > filterLocalDateAtMidnight) {
    return 1;
  } else {
    return 0;
  }
};

export function classComparator(valueA: any, valueB: any): number {
  const strA = typeof valueA === 'string' ? valueA : String(valueA);
  const strB = typeof valueB === 'string' ? valueB : String(valueB);

  const isEmptyA = !strA || strA.trim() === '';
  const isEmptyB = !strB || strB.trim() === '';

  if (isEmptyA && isEmptyB) {
    return 0;
  }
  if (isEmptyA) {
    return 1;
  }
  if (isEmptyB) {
    return -1;
  }
  return strA.localeCompare(strB, undefined, { numeric: true });
}

export function titleComparator(valueA: any, valueB: any): number {
  const titleA = valueA?.title || '';
  const titleB = valueB?.title || '';
  const isSystemA = valueA?.systemGenerated || false;
  const isSystemB = valueB?.systemGenerated || false;

  if (isSystemA && !isSystemB) {
    return 1;
  }
  if (!isSystemA && isSystemB) {
    return -1;
  }

  return titleA.localeCompare(titleB);
}

export class FilterOptions {
  static readonly OPTIONS = ['equals', 'inRange', 'lessThan', 'greaterThan'];
}
